import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Autocomplete,
} from "@mui/material";
import {
  FaPlus,
  FaTimes,
  FaEdit,
  FaCertificate,
  FaChevronDown,
  FaChevronUp,
  FaAward,
} from "react-icons/fa";
import "./CertificationList.css";

const CertificationList = ({
  certifications,
  onAddCertification,
  onRemoveCertification,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [formData, setFormData] = useState({
    diplomas: [],
    otherDiploma: "",
    discipline: "Sport & animation",
    level: "Intermédiaire",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showExpired, setShowExpired] = useState(false);

  const toSlug = (value) => {
    if (!value) return "";
    return value
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/['’]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/_+/g, "_");
  };

  const sortLabels = (items) =>
    [...items].sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base" }),
    );

  const diplomaSuggestions = {
    "Diplômes et certifications – (Sport & animation)": sortLabels([
      "BMF (Brevet de Moniteur de Football)",
      "BPJEPS (toutes mentions)",
      "BPJEPS AF (Activités de la Forme)",
      "BPJEPS APT (Activités Physiques pour Tous)",
      "BPJEPS Sports Collectifs",
      "CPJEPS",
      "CQP ALS (Animateur de Loisirs Sportifs)",
      "CQP Instructeur Fitness",
      "DEJEPS (toutes mentions)",
      "DES Football",
      "DESJEPS",
      "Diplôme universitaire sport / santé / performance",
      "Licence STAPS",
      "Master STAPS",
    ]),
    "Diplômes et certifications – Football / Futsal": sortLabels([
      "Licence FIFA Futsal",
      "Licence UEFA A",
      "Licence UEFA B",
      "Licence UEFA C",
      "Licence UEFA Futsal",
      "Licence UEFA Pro",
    ]),
    "Diplômes et certifications – Autres disciplines": sortLabels([
      "Certificat d’entraîneur fédéral",
      "Certificat d’initiateur sportif",
      "Diplôme fédéral (toutes fédérations)",
      "Diplôme professionnel spécifique à la discipline",
    ]),
    "Diplômes – Santé, performance et bien-être": sortLabels([
      "Diplôme APA (Activité Physique Adaptée)",
      "Diplôme de coach mental",
      "Diplôme de nutrition du sport",
      "Diplôme de préparateur physique",
      "Diplôme de psychologie du sport",
      "Diplôme de rééducation sportive",
      "Diplôme universitaire de préparation physique",
    ]),
    "Certifications transversales obligatoires / recommandées": sortLabels([
      "Certification Safeguarding / Protection des publics",
      "Certificat d’aptitude médicale à l’encadrement",
      "Formation éthique et intégrité sportive",
      "Formation prévention des violences",
      "PSC1 – Premiers secours",
      "SST – Sauveteur Secouriste du Travail",
    ]),
    "Diplômes internationaux / autres": sortLabels([
      "Certificat professionnel reconnu par une fédération internationale",
      "Certification internationale privée (CrossFit, Pilates, Yoga, etc.)",
      "Diplôme étranger reconnu par équivalence",
    ]),
    Autre: ["Diplôme non listé (à préciser)"],
  };

  const allDiplomaOptions = Object.entries(diplomaSuggestions).flatMap(
    ([category, list]) =>
      list.map((label) => ({
        label,
        category,
        slug:
          label === "Diplôme non listé (à préciser)" ? "autre" : toSlug(label),
      })),
  );

  const disciplineOptions = [
    "Sport & animation",
    "Football / Futsal",
    "Autres disciplines",
    "Santé / performance / bien-être",
    "Transversales",
    "Internationaux / autres",
  ];

  const levelOptions = ["Débutant", "Intermédiaire", "Avancé", "Expert"];

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      diplomas: [],
      otherDiploma: "",
      discipline: "Sport & animation",
      level: "Intermédiaire",
    });
    setValidationErrors({});
    setEditingCert(null);
  };

  // Valider le formulaire
  const validateForm = () => {
    const errors = {};

    if (!Array.isArray(formData.diplomas) || formData.diplomas.length === 0) {
      errors.diplomas = "Veuillez sélectionner au moins un diplôme";
    }

    if (
      Array.isArray(formData.diplomas) &&
      formData.diplomas.some((d) => d.slug === "autre") &&
      !formData.otherDiploma.trim()
    ) {
      errors.otherDiploma = "Veuillez préciser le diplôme";
    }

    if (!formData.discipline) {
      errors.discipline = "Veuillez sélectionner une discipline";
    }

    if (!formData.level) {
      errors.level = "Veuillez sélectionner un niveau";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Gérer la soumission du formulaire
  const handleSubmit = () => {
    if (!validateForm()) return;

    const now = new Date().toISOString();
    const items = formData.diplomas
      .filter((d) => d && d.slug !== "autre")
      .map((d) => ({
        id: Date.now().toString() + "-" + d.slug,
        name: d.label,
        slug: d.slug,
        discipline: formData.discipline,
        level: formData.level,
        needsAdminValidation: false,
        dateAdded: now,
      }));

    if (formData.diplomas.some((d) => d.slug === "autre")) {
      const label = formData.otherDiploma.trim();
      items.push({
        id: Date.now().toString() + "-autre",
        name: label,
        slug: toSlug(label),
        discipline: formData.discipline,
        level: formData.level,
        needsAdminValidation: true,
        dateAdded: now,
      });
    }

    if (editingCert) {
      onRemoveCertification(editingCert.id);
      if (items[0]) {
        items[0] = {
          ...items[0],
          id: editingCert.id,
          dateAdded: editingCert.dateAdded || items[0].dateAdded,
        };
      }
    }

    items.forEach((cert) => onAddCertification(cert));
    resetForm();
    setShowAddForm(false);
  };

  // Ouvrir le formulaire d'édition
  const handleEdit = (cert) => {
    setFormData({
      diplomas: [
        {
          label: cert.needsAdminValidation
            ? "Diplôme non listé (à préciser)"
            : cert.name,
          slug: cert.needsAdminValidation
            ? "autre"
            : cert.slug || toSlug(cert.name),
          category: cert.needsAdminValidation ? "Autre" : "Sélection",
        },
      ],
      otherDiploma: cert.needsAdminValidation ? cert.name : "",
      discipline: cert.discipline || "Sport & animation",
      level: cert.level || "Intermédiaire",
    });
    setEditingCert(cert);
    setShowAddForm(true);
  };

  // Fermer le formulaire
  const handleCloseForm = () => {
    resetForm();
    setShowAddForm(false);
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Filtrer les certifications
  const activeCertifications = certifications;
  const expiredCertifications = [];
  const expiringSoonCertifications = [];

  // Vérifier si une certification expire bientôt (désactivé)
  const isExpiringSoon = () => false;

  return (
    <Box className="certification-list">
      <Box className="section-header">
        <Typography variant="h6" className="section-title">
          <FaCertificate className="section-icon" />
          Certifications et diplômes
        </Typography>

        <Button
          variant="outlined"
          startIcon={<FaPlus />}
          onClick={() => setShowAddForm(true)}
          className="add-cert-button"
        >
          Ajouter
        </Button>
      </Box>

      {/* Alertes pour les certifications qui expirent */}
      {expiringSoonCertifications.length > 0 && (
        <Alert severity="warning" className="expiry-alert">
          <Typography variant="body2">
            <strong>{expiringSoonCertifications.length}</strong>{" "}
            certification(s) expire(nt) dans les 3 prochains mois.
          </Typography>
        </Alert>
      )}

      {/* Liste des certifications actives */}
      {activeCertifications.length > 0 ? (
        <Box className="certifications-grid">
          {activeCertifications.map((cert) => (
            <Paper
              key={cert.id}
              className={`certification-card ${isExpiringSoon(cert) ? "expiring-soon" : ""}`}
              elevation={1}
            >
              <Box className="cert-header">
                <Box className="cert-type">
                  <Chip label="Diplôme" size="small" className="type-chip" />
                  {isExpiringSoon(cert) && (
                    <Chip
                      label="Expire bientôt"
                      size="small"
                      color="warning"
                      className="warning-chip"
                    />
                  )}
                  {cert.needsAdminValidation && (
                    <Chip
                      label="Validation admin"
                      size="small"
                      color="warning"
                      className="warning-chip"
                    />
                  )}
                </Box>

                <Box className="cert-actions">
                  <Tooltip title="Modifier">
                    <IconButton
                      onClick={() => handleEdit(cert)}
                      className="edit-button"
                      size="small"
                    >
                      <FaEdit />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Supprimer">
                    <IconButton
                      onClick={() => onRemoveCertification(cert.id)}
                      className="remove-button"
                      size="small"
                    >
                      <FaTimes />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <Box className="cert-content">
                <Typography variant="h6" className="cert-name">
                  <FaAward className="cert-icon" />
                  {cert.name}
                </Typography>

                <Box className="cert-type" sx={{ mt: 1 }}>
                  {cert.discipline && (
                    <Chip
                      label={cert.discipline}
                      size="small"
                      variant="outlined"
                      className="type-chip"
                    />
                  )}
                  {cert.level && (
                    <Chip
                      label={cert.level}
                      size="small"
                      variant="outlined"
                      className="type-chip"
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      ) : (
        <Alert severity="info" className="empty-state">
          <Typography variant="body2">
            Aucune certification ajoutée. Ajoutez vos diplômes et certifications
            pour valoriser votre profil.
          </Typography>
        </Alert>
      )}

      {/* Certifications expirées */}
      {expiredCertifications.length > 0 && (
        <Box className="expired-section">
          <Button
            variant="text"
            onClick={() => setShowExpired(!showExpired)}
            endIcon={showExpired ? <FaChevronUp /> : <FaChevronDown />}
            className="expired-toggle"
          >
            Certifications expirées ({expiredCertifications.length})
          </Button>

          <Collapse in={showExpired}>
            <Box className="expired-certifications">
              {expiredCertifications.map((cert) => (
                <Paper
                  key={cert.id}
                  className="certification-card expired"
                  elevation={1}
                >
                  <Box className="cert-header">
                    <Chip
                      label="Expirée"
                      size="small"
                      color="error"
                      className="expired-chip"
                    />

                    <IconButton
                      onClick={() => onRemoveCertification(cert.id)}
                      className="remove-button"
                      size="small"
                    >
                      <FaTimes />
                    </IconButton>
                  </Box>

                  <Box className="cert-content">
                    <Typography variant="body1" className="cert-name">
                      {cert.name}
                    </Typography>
                    <Typography variant="body2" className="cert-organization">
                      {cert.organization}
                    </Typography>
                    <Typography variant="caption" className="expiry-date">
                      Expirée le {formatDate(cert.expiryDate)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          </Collapse>
        </Box>
      )}

      {/* Dialog d'ajout/édition */}
      <Dialog
        open={showAddForm}
        onClose={handleCloseForm}
        maxWidth="sm"
        fullWidth
        className="cert-dialog"
      >
        <DialogTitle>
          <Box className="dialog-title">
            <FaCertificate className="dialog-icon" />
            {editingCert
              ? "Modifier la certification"
              : "Ajouter une certification"}
          </Box>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={3} className="cert-form">
            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={allDiplomaOptions}
                groupBy={(option) => option.category}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.slug === value.slug
                }
                value={formData.diplomas}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, diplomas: newValue || [] });
                  setValidationErrors((prev) => ({ ...prev, diplomas: "" }));
                  if (!newValue?.some((d) => d.slug === "autre")) {
                    setFormData((prev) => ({ ...prev, otherDiploma: "" }));
                    setValidationErrors((prev) => ({
                      ...prev,
                      otherDiploma: "",
                    }));
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      {...getTagProps({ index })}
                      key={option.slug}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Diplômes / certifications *"
                    error={!!validationErrors.diplomas}
                    helperText={validationErrors.diplomas}
                    placeholder="Sélectionnez un ou plusieurs diplômes"
                  />
                )}
              />
            </Grid>

            {formData.diplomas.some((d) => d.slug === "autre") && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Diplôme non listé (à préciser) *"
                  value={formData.otherDiploma}
                  onChange={(e) => {
                    setFormData({ ...formData, otherDiploma: e.target.value });
                    setValidationErrors((prev) => ({
                      ...prev,
                      otherDiploma: "",
                    }));
                  }}
                  error={!!validationErrors.otherDiploma}
                  helperText={
                    validationErrors.otherDiploma ||
                    "Ce diplôme sera soumis à validation admin"
                  }
                  placeholder="Ex: Diplôme spécifique..."
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!validationErrors.discipline}>
                <InputLabel>Discipline *</InputLabel>
                <Select
                  value={formData.discipline}
                  onChange={(e) => {
                    setFormData({ ...formData, discipline: e.target.value });
                    setValidationErrors((prev) => ({
                      ...prev,
                      discipline: "",
                    }));
                  }}
                  label="Discipline *"
                >
                  {disciplineOptions.map((disc) => (
                    <MenuItem key={disc} value={disc}>
                      {disc}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.discipline && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, ml: 1.75 }}
                  >
                    {validationErrors.discipline}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Niveau *"
                value={formData.level}
                onChange={(e) => {
                  setFormData({ ...formData, level: e.target.value });
                  setValidationErrors((prev) => ({ ...prev, level: "" }));
                }}
                error={!!validationErrors.level}
                helperText={validationErrors.level}
              >
                {levelOptions.map((lvl) => (
                  <MenuItem key={lvl} value={lvl}>
                    {lvl}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className="dialog-actions">
          <Button onClick={handleCloseForm} className="cancel-button">
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            className="save-button"
          >
            {editingCert ? "Modifier" : "Ajouter"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CertificationList;
